/* eslint-disable no-unused-vars */
import { calculateWeightedAveragePPD } from "./financialMetrics";

/**
 * Calculates consolidated data for a facility group
 *
 * @param {Object} group - The group object
 * @param {Object} dealData - The deal data containing facilities
 * @return {Object} Consolidated facility data for the group
 */

/**
 * Helper Functions for Metric Calculations
 */

// Extract numeric value from string or number
function extractNumericValue(value) {
  if (value === null || value === undefined) return null;

  if (typeof value === "string") {
    const match = value.match(/[\d.]+/);
    return match ? parseFloat(match[0]) : null;
  }
  return typeof value === "number" ? value : null;
}

// Find a metric by type in a category
function findMetric(categories, category, type) {
  return categories?.[category]?.find((item) => item.Type === type);
}

// Get a numeric value from a metric safely
function getMetricValue(metric, property = "Annual") {
  return parseFloat(metric?.[property]) || 0;
}

// Calculate average from an array of values
function calculateAverage(values) {
  if (!values || values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

export const calculateGroupData = (group, dealData = null) => {
  // Use provided dealData or fall back to currentDeal
  if (!group) {
    return null;
  }

  if (!dealData) {
    return null;
  }

  if (!dealData.facilities) {
    return null;
  }

  // Get all facilities in the group
  const facilities = group.facilityIds
    .map((facilityId) => {
      // Find the facility that matches this ID
      const facilityEntry = Object.entries(dealData.facilities).find(
        ([key, facility]) => key === facilityId
      );

      if (!facilityEntry) {
        return null;
      }
      return facilityEntry[1];
    })
    .filter(Boolean);

  if (facilities.length === 0) {
    return null;
  }

  // Create a consolidated facility object that includes group information
  const consolidatedFacility = {
    details: {
      ...facilities[0].details,
      Facilities: `Group: ${group.name}`,
      isGroup: true,
      groupId: group.id,
      State: facilities[0].details?.State || "",
      "City, State": "", // Keep the City, State field blank for groups
    },
    periods: {},
    hudDebt: {
      Categories: {},
    },
  };

  // --- CONSOLIDATE DETAILS ---

  // Sum all bed-related fields in the details object
  const bedFields = [
    "Total Beds",
    "SNF Beds",
    "ALF Beds",
    "ILF Beds",
    "MC Beds",
  ];

  bedFields.forEach((bedField) => {
    consolidatedFacility.details[bedField] = facilities.reduce(
      (sum, facility) => sum + (parseInt(facility.details?.[bedField]) || 0),
      0
    );
  });

  // Handle State as an aggregated list of unique states
  const states = facilities
    .map((facility) => facility.details?.State)
    .filter(Boolean)
    .filter((value, index, self) => self.indexOf(value) === index); // Remove duplicates

  if (states.length > 0) {
    consolidatedFacility.details.State = states.join(", ");
  }

  // Handle Years Renovated as an aggregated list
  const yearsRenovated = facilities
    .map((facility) => facility.details?.["Years Renovated"])
    .filter((year) => year !== undefined && year !== null && year !== "");

  if (yearsRenovated.length > 0) {
    consolidatedFacility.details["Years Renovated"] = yearsRenovated.join(", ");
  }

  // Calculate simple averages for star ratings (not weighted by bed count)
  const starRatingFields = [
    "Star Rating",
    "Health Inspection Star Rating",
    "Staffing Star Rating",
    "QM Star Rating",
    "Quality Measures Star Rating",
  ];

  starRatingFields.forEach((ratingField) => {
    const ratings = facilities
      .map((facility) => extractNumericValue(facility.details?.[ratingField]))
      .filter((value) => value !== null);

    consolidatedFacility.details[ratingField] =
      ratings.length > 0 ? Math.round(calculateAverage(ratings) * 10) / 10 : 0;
  });

  // Helper function to extract Medicaid Rate from a facility
  function extractMedicaidRate(facility) {
    if (!facility.hudDebt?.Categories) return null;

    // Find Medicaid category
    const medicaidCategory = Object.entries(facility.hudDebt.Categories).find(
      ([key]) => key.startsWith("Med")
    );

    if (!medicaidCategory) return null;

    // Find rate item
    const [_, data] = medicaidCategory;
    const rateItem = data.find(
      (item) =>
        item.Type?.includes("Med") ||
        item.Type?.includes("Total Rate") ||
        item.Type?.includes("Current")
    );

    return rateItem?.Amount ? parseFloat(rateItem.Amount) : null;
  }

  // Calculate simple average for Medicaid Rate
  const medicaidRates = facilities
    .map(extractMedicaidRate)
    .filter((rate) => rate !== null);

  if (medicaidRates.length > 0) {
    consolidatedFacility.details["Medicaid Rate"] = Math.round(
      calculateAverage(medicaidRates)
    ).toString();
  }

  // Average for HPPD values
  const hppdFields = [
    "Nursing HPPD - Actual",
    "Nursing HPPD - Weekend",
    "Nursing HPPD - Expected",
  ];

  hppdFields.forEach((field) => {
    const values = facilities
      .map((facility) => extractNumericValue(facility.details?.[field]))
      .filter((value) => value !== null);

    if (values.length > 0) {
      // Round to 2 decimal places
      consolidatedFacility.details[field] =
        Math.round(calculateAverage(values) * 100) / 100;
    }
  });

  // Average for "Years Built" field
  const yearBuiltValues = facilities
    .map((facility) => extractNumericValue(facility.details?.["Years Built"]))
    .filter((value) => value !== null);

  if (yearBuiltValues.length > 0) {
    consolidatedFacility.details["Years Built"] =
      calculateAverage(yearBuiltValues);
  }

  // --- CONSOLIDATE PERIODS DATA ---

  // If you have period data, consolidate it
  if (facilities[0].periods) {
    // Get all periods across all facilities
    const allPeriods = new Set();
    facilities.forEach((facility) => {
      Object.keys(facility.periods || {}).forEach((period) => {
        allPeriods.add(period);
      });
    });

    // Process each period
    [...allPeriods].forEach((period) => {
      consolidatedFacility.periods[period] = { Categories: {} };

      // Process each category (Revenue, Expenses, Occupancy)
      ["Occupancy", "Revenue", "Expenses"].forEach((category) => {
        // Changed order to process Occupancy first
        consolidatedFacility.periods[period].Categories[category] = [];

        // Get all metric types across all facilities
        const metricTypes = new Set();
        facilities.forEach((facility) => {
          const metrics =
            facility.periods[period]?.Categories?.[category] || [];
          metrics.forEach((metric) => metricTypes.add(metric.Type));
        });

        // Special handling for Occupancy category to calculate percentages
        if (category === "Occupancy") {
          // Get total annual days from the Total metric
          const totalAnnualDays = facilities.reduce((sum, facility) => {
            const totalMetric = facility.periods[
              period
            ]?.Categories?.Occupancy?.find((item) => item.Type === "Total");
            return sum + (parseFloat(totalMetric?.Annual) || 0);
          }, 0);

          // Get bed counts
          const totalBeds = facilities.reduce((sum, facility) => {
            const snfBeds = parseInt(facility.details?.["SNF Beds"]) || 0;
            const alfBeds = parseInt(facility.details?.["ALF Beds"]) || 0;
            const ilfBeds = parseInt(facility.details?.["ILF Beds"]) || 0;
            const mcBeds = parseInt(facility.details?.["MC Beds"]) || 0;
            return sum + snfBeds + alfBeds + ilfBeds + mcBeds;
          }, 0);

          // Calculate occupancy percentage
          if (totalBeds > 0) {
            const occupancyPercentage =
              (totalAnnualDays / (totalBeds * 365)) * 100;
            consolidatedFacility.periods[period].Categories[category].push({
              Type: "Occupancy",
              Annual: totalAnnualDays,
              Percentages: occupancyPercentage.toFixed(2),
            });
          }

          // Calculate skilled percentage using total skilled days
          let totalSkilledDays = 0;
          let totalSNFDays = 0;

          // Sum up skilled days and SNF total days across all facilities
          facilities.forEach((facility) => {
            // Get skilled days components
            const medicareDaysMetric = facility.periods[
              period
            ]?.Categories?.Occupancy?.find(
              (item) =>
                item.Type === "Medicare Days" || item.Type === "Medicare"
            );
            const medicareHMOMetric = facility.periods[
              period
            ]?.Categories?.Occupancy?.find(
              (item) =>
                item.Type === "Medicare HMO Days" ||
                item.Type === "Medicare HMO"
            );
            const commercialInsuranceMetric = facility.periods[
              period
            ]?.Categories?.Occupancy?.find(
              (item) =>
                item.Type === "Commercial Insurance Days" ||
                item.Type === "Commercial Insurance"
            );

            // Sum up skilled days
            const medicareDays = parseFloat(medicareDaysMetric?.Annual) || 0;
            const medicareHMODays = parseFloat(medicareHMOMetric?.Annual) || 0;
            const commercialDays =
              parseFloat(commercialInsuranceMetric?.Annual) || 0;
            totalSkilledDays += medicareDays + medicareHMODays + commercialDays;

            // Get SNF total days
            const snfTotalMetric = facility.periods[
              period
            ]?.Categories?.Occupancy?.find((item) => item.Type === "SNF Total");
            totalSNFDays += parseFloat(snfTotalMetric?.Annual) || 0;
          });

          // Calculate skilled percentage
          if (totalSNFDays > 0) {
            const skilledPercentage = (totalSkilledDays / totalSNFDays) * 100;
            consolidatedFacility.periods[period].Categories[category].push({
              Type: "Skilled",
              Annual: totalSkilledDays,
              Percentages: skilledPercentage.toFixed(2),
            });
          }
        }

        // Consolidate each metric type
        [...metricTypes].forEach((metricType) => {
          // Skip Occupancy and Skilled types as they're handled above
          if (
            category === "Occupancy" &&
            (metricType === "Occupancy" || metricType === "Skilled")
          ) {
            return;
          }

          // Find this metric in all facilities
          const metricsAcrossFacilities = facilities
            .map((facility) =>
              facility.periods[period]?.Categories?.[category]?.find(
                (m) => m.Type === metricType
              )
            )
            .filter(Boolean);

          if (metricsAcrossFacilities.length === 0) return;

          // Calculate consolidated values
          const consolidatedMetric = { Type: metricType };

          // Sum Annual values - always sum these, with null/undefined handling
          consolidatedMetric.Annual = metricsAcrossFacilities.reduce(
            (sum, metric) => sum + (parseFloat(metric?.Annual) || 0),
            0
          );

          // Get total patient days for correct PPD calculations
          let totalPatientDays = 0;

          // Get the total days metric after Occupancy is processed
          if (consolidatedFacility.periods[period]?.Categories?.Occupancy) {
            const totalDaysMetric = consolidatedFacility.periods[
              period
            ].Categories.Occupancy.find((item) => item.Type === "Total");
            totalPatientDays = totalDaysMetric
              ? parseFloat(totalDaysMetric.Annual) || 0
              : 0;
          }

          // Determine how to handle PPD values based on metric type
          if (metricsAcrossFacilities.some((m) => m?.PPD !== undefined)) {
            // For census, days, and bed count metrics, sum PPD values
            if (
              category === "Occupancy" ||
              metricType.toLowerCase().includes("day") ||
              metricType.toLowerCase().includes("bed") ||
              metricType.toLowerCase().includes("census")
            ) {
              // For these metrics, PPD represents a daily count, so sum them
              consolidatedMetric.PPD = parseFloat(
                metricsAcrossFacilities
                  .reduce(
                    (sum, metric) => sum + (parseFloat(metric?.PPD) || 0),
                    0
                  )
                  .toFixed(2)
              );
            }
            // For EBITDAR, calculate as a ratio to revenue
            else if (
              category === "Expenses" &&
              (metricType === "EBITDAR" || metricType === "Adjusted EBITDAR")
            ) {
              const revenueType = metricType.includes("Adjusted")
                ? "Adjusted Revenue"
                : "Total";
              const revenueMetric = consolidatedFacility.periods[
                period
              ]?.Categories?.Revenue?.find((item) => item.Type === revenueType);
              const revenue = revenueMetric
                ? parseFloat(revenueMetric.Annual) || 0
                : 0;

              if (revenue > 0 && consolidatedMetric.Annual) {
                // Round to nearest percentage (convert to percentage first, then round)
                const marginPercentage =
                  (consolidatedMetric.Annual / revenue) * 100;
                consolidatedMetric.PPD = parseFloat(
                  (Math.round(marginPercentage) / 100).toFixed(2)
                );
              } else if (totalPatientDays > 0) {
                consolidatedMetric.PPD = parseFloat(
                  (consolidatedMetric.Annual / totalPatientDays).toFixed(2)
                );
              } else {
                // If no revenue or days, calculate weighted average
                const weightedPPD = calculateWeightedAveragePPD(
                  facilities,
                  metricsAcrossFacilities
                );
                consolidatedMetric.PPD = weightedPPD;
              }
            }
            // For Revenue metrics, use simple average of PPD values
            else if (category === "Revenue") {
              // Special handling for payor-specific metrics
              const payorMetrics = [
                "Medicaid",
                "Medicaid HMO",
                "Hospice",
                "Medicare",
                "Medicare HMO",
                "Private",
                "Insurance",
                "Veterans",
              ];

              const monthlyRateMetrics = [
                "Assisted Living - Waiver",
                "Assisted Living",
                "Memory Care",
                "Independent Living",
              ];

              if (monthlyRateMetrics.includes(metricType)) {
                // Find corresponding census metric in Occupancy

                let censusMetricType = metricType;
                // Special handling for Assisted Living - Waiver
                if (metricType === "Assisted Living - Waiver") {
                  censusMetricType = "Assisted Living Days - Waiver";
                } else {
                  // For all other monthly rate metrics, just append "Days"
                  censusMetricType = `${metricType} Days`;
                }

                const censusMetric = consolidatedFacility.periods[
                  period
                ]?.Categories?.Occupancy?.find(
                  (item) => item.Type === censusMetricType
                );

                const censusPPD = parseFloat(censusMetric?.PPD) || 0;

                if (censusPPD > 0 && consolidatedMetric.Annual > 0) {
                  const monthlyRate =
                    consolidatedMetric.Annual / (censusPPD * 12);
                  consolidatedMetric.PPD = `$${monthlyRate.toLocaleString(
                    "en-US",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )} /Month`;
                } else {
                  consolidatedMetric.PPD = null;
                }
              } else if (payorMetrics.includes(metricType)) {
                // Find corresponding days metric in Occupancy
                let daysMetricType;
                if (metricType === "Insurance") {
                  daysMetricType = "Commercial Insurance Days";
                } else if (metricType === "Veterans") {
                  daysMetricType = "VA Days";
                } else {
                  daysMetricType = `${metricType} Days`;
                }

                const daysMetric = consolidatedFacility.periods[
                  period
                ]?.Categories?.Occupancy?.find(
                  (item) =>
                    item.Type === daysMetricType || item.Type === metricType
                );

                const days = parseFloat(daysMetric?.Annual) || 0;

                if (days > 0 && consolidatedMetric.Annual > 0) {
                  const ppd = parseFloat(
                    (consolidatedMetric.Annual / days).toFixed(2)
                  );
                  consolidatedMetric.PPD = ppd;
                } else {
                  consolidatedMetric.PPD = null;
                }
              } else {
                // For all other revenue metrics, use simple average of PPD values
                // Special handling for specific revenue totals
                if (metricType === "Total") {
                  // Get total days from Occupancy
                  const totalDaysMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Occupancy?.find(
                    (item) => item.Type === "Total"
                  );
                  const totalDays = parseFloat(totalDaysMetric?.Annual) || 0;

                  if (totalDays > 0 && consolidatedMetric.Annual > 0) {
                    consolidatedMetric.PPD = parseFloat(
                      (consolidatedMetric.Annual / totalDays).toFixed(2)
                    );
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else if (metricType === "Total SNF Revenue") {
                  // Get SNF total days from Occupancy
                  const snfTotalMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Occupancy?.find(
                    (item) => item.Type === "SNF Total"
                  );
                  const snfTotalDays = parseFloat(snfTotalMetric?.Annual) || 0;

                  if (snfTotalDays > 0 && consolidatedMetric.Annual > 0) {
                    consolidatedMetric.PPD = parseFloat(
                      (consolidatedMetric.Annual / snfTotalDays).toFixed(2)
                    );
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else if (
                  metricType === "Total ALF Revenue" ||
                  metricType === "Total ILF Revenue"
                ) {
                  // Get corresponding days from Occupancy
                  const daysType =
                    metricType === "Total ALF Revenue"
                      ? "AL Total"
                      : "IL Total";
                  const daysMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Occupancy?.find(
                    (item) => item.Type === daysType
                  );
                  const days = parseFloat(daysMetric?.PPD) || 0;

                  if (days > 0 && consolidatedMetric.Annual > 0) {
                    const monthlyRate = consolidatedMetric.Annual / (days * 12);
                    consolidatedMetric.PPD = `$${monthlyRate.toLocaleString(
                      "en-US",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )} /Month`;
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else if (metricType === "Adjusted Revenue") {
                  // Get total days from Occupancy
                  const totalDaysMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Occupancy?.find(
                    (item) => item.Type === "Total"
                  );
                  const totalDays = parseFloat(totalDaysMetric?.Annual) || 0;

                  if (totalDays > 0 && consolidatedMetric.Annual > 0) {
                    consolidatedMetric.PPD = parseFloat(
                      (consolidatedMetric.Annual / totalDays).toFixed(2)
                    );
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else if (
                  metricType === "Other Revenue" ||
                  metricType === "Ancillary"
                ) {
                  // Get total days from Occupancy
                  const totalDaysMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Occupancy?.find(
                    (item) => item.Type === "Total"
                  );
                  const totalDays = parseFloat(totalDaysMetric?.Annual) || 0;

                  if (totalDays > 0 && consolidatedMetric.Annual > 0) {
                    consolidatedMetric.PPD = parseFloat(
                      (consolidatedMetric.Annual / totalDays).toFixed(2)
                    );
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else {
                  // Original logic for other revenue metrics
                  const ppds = metricsAcrossFacilities
                    .map((metric) => {
                      if (typeof metric.PPD === "string") {
                        const match = metric.PPD.match(/[\d.]+/);
                        return match ? parseFloat(match[0]) : null;
                      }
                      return typeof metric.PPD === "number" ? metric.PPD : null;
                    })
                    .filter((ppd) => ppd !== null);

                  if (ppds.length > 0) {
                    const averagePPD =
                      ppds.reduce((sum, ppd) => sum + ppd, 0) / ppds.length;
                    consolidatedMetric.PPD = parseFloat(averagePPD.toFixed(2));
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                }
              }
            }
            // For all other Expenses metrics, calculate based on total days
            else if (category === "Expenses") {
              if (totalPatientDays > 0) {
                // Check if this is a payroll taxes and benefits metric that needs percentage calculation
                if (
                  metricType === "Payroll Taxes Benefits" ||
                  metricType === "Payroll Taxes" ||
                  metricType === "Workers Comp" ||
                  metricType === "Benefits" ||
                  metricType === "Management Fees"
                ) {
                  // For these metrics, we want to average the percentages from all facilities
                  const percentages = metricsAcrossFacilities
                    .map((metric) => {
                      if (metric.PPD) {
                        if (typeof metric.PPD === "string") {
                          const match = metric.PPD.match(/[\d.]+/);
                          return match ? parseFloat(match[0]) : null;
                        }
                        return parseFloat(metric.PPD) || null;
                      }
                      // Then try Percentages field
                      if (metric.Percentages) {
                        if (typeof metric.Percentages === "string") {
                          const match = metric.Percentages.match(/[\d.]+/);
                          return match ? parseFloat(match[0]) : null;
                        }
                        return parseFloat(metric.Percentages) || null;
                      }
                      return null;
                    })
                    .filter((value) => value !== null);

                  if (percentages.length > 0) {
                    const averagePercentage =
                      percentages.reduce((sum, value) => sum + value, 0) /
                      percentages.length;
                    consolidatedMetric.PPD = null; // Clear PPD since we're using percentage
                    consolidatedMetric.Percentages = `${averagePercentage.toFixed(
                      1
                    )}%`;
                  } else {
                    consolidatedMetric.PPD = null;
                    consolidatedMetric.Percentages = "0%";
                  }
                } else if (
                  metricType === "Nursing and Medical" ||
                  metricType === "Nursing Admin" ||
                  metricType === "Nursing Agency"
                ) {
                  // Get SNF total days from Occupancy data
                  const snfMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Occupancy?.find(
                    (item) => item.Type === "SNF Total"
                  );

                  const snfTotalDays = snfMetric?.Annual || 0;

                  if (snfTotalDays > 0) {
                    consolidatedMetric.PPD = parseFloat(
                      (consolidatedMetric.Annual / snfTotalDays).toFixed(2)
                    );
                  } else {
                    consolidatedMetric.PPD = 0;
                  }
                } else if (
                  metricType === "Property Insurance" ||
                  metricType === "Insurance Expense"
                ) {
                  // Sum up Licensed Beds from all facilities in the group
                  const totalBeds = facilities.reduce((sum, facility) => {
                    return (
                      sum + (parseInt(facility.details?.["Total Beds"]) || 0)
                    );
                  }, 0);

                  if (totalBeds > 0 && consolidatedMetric.Annual > 0) {
                    const calculatedPPD = parseFloat(
                      (consolidatedMetric.Annual / totalBeds).toFixed(2)
                    );
                    consolidatedMetric.PPD = `${calculatedPPD} Per Bed`;
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else if (metricType === "Assessment Tax") {
                  // Sum up SNF Beds from all facilities in the group
                  const totalSNFBeds = facilities.reduce((sum, facility) => {
                    return (
                      sum + (parseInt(facility.details?.["SNF Beds"]) || 0)
                    );
                  }, 0);

                  if (totalSNFBeds > 0 && consolidatedMetric.Annual > 0) {
                    const calculatedPPD = parseFloat(
                      (consolidatedMetric.Annual / totalSNFBeds / 365).toFixed(
                        2
                      )
                    );
                    consolidatedMetric.PPD = `${calculatedPPD} /Bed /Day`;
                  } else {
                    consolidatedMetric.PPD = null;
                  }
                } else if (metricType === "Bad Debt") {
                  // Get Total Revenue and Other Revenue from Revenue category
                  const totalRevenueMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Revenue?.find((item) => item.Type === "Total");
                  const otherRevenueMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Revenue?.find(
                    (item) => item.Type === "Other Revenue"
                  );

                  const totalRevenue =
                    parseFloat(totalRevenueMetric?.Annual) || 0;
                  const otherRevenue =
                    parseFloat(otherRevenueMetric?.Annual) || 0;
                  const adjustedRevenue = totalRevenue - otherRevenue;

                  if (adjustedRevenue > 0 && consolidatedMetric.Annual > 0) {
                    const percentage =
                      (consolidatedMetric.Annual / adjustedRevenue) * 100;
                    consolidatedMetric.PPD = null; // Clear PPD since we're using percentage
                    consolidatedMetric.Percentages = `${percentage.toFixed(
                      1
                    )}%`;
                  } else {
                    consolidatedMetric.PPD = null;
                    consolidatedMetric.Percentages = "0%";
                  }
                } else if (
                  metricType === "Therapy" ||
                  metricType === "Pharmacy Expense" ||
                  metricType === "Other Ancillaries"
                ) {
                  consolidatedMetric.PPD = _calculateSkilledMetricPPD(
                    consolidatedMetric,
                    consolidatedFacility,
                    period
                  );
                } else if (metricType === "Part B Expense") {
                  // Get ancillary days from Occupancy
                  const ancillaryMetric = consolidatedFacility.periods[
                    period
                  ]?.Categories?.Revenue?.find(
                    (item) => item.Type === "Ancillary"
                  );
                  const ancillaryRevenue =
                    parseFloat(ancillaryMetric?.Annual) || 0;

                  if (ancillaryRevenue > 0 && consolidatedMetric.Annual > 0) {
                    // Calculate PPD using formula: annual / (ancillary days / 0.8)
                    const adjustedDays = ancillaryRevenue / 0.8;
                    const percentage =
                      (consolidatedMetric.Annual / adjustedDays) * 100;
                    consolidatedMetric.PPD = null; // Clear PPD since we're using percentage
                    consolidatedMetric.Percentages = `${percentage.toFixed(
                      1
                    )}%`;
                  } else {
                    consolidatedMetric.PPD = null;
                    consolidatedMetric.Percentages = "0%";
                  }
                } else if (metricType === "AL Nursing") {
                  consolidatedMetric.PPD = calculateALNursingPPD(
                    consolidatedMetric,
                    consolidatedFacility,
                    facilities,
                    period
                  );
                } else if (metricType === "General Nursing Expenses") {
                  consolidatedMetric.PPD = calculateGeneralNursingPPD(
                    consolidatedMetric,
                    consolidatedFacility,
                    facilities,
                    period
                  );
                } else if (metricType === "Total Adjustments") {
                  // Total Adjustments should have blank PPD
                  consolidatedMetric.PPD = null;
                } else {
                  // Standard calculation for other metrics
                  consolidatedMetric.PPD = calculateStandardPPD(
                    consolidatedMetric,
                    metricsAcrossFacilities,
                    facilities,
                    totalPatientDays,
                    period
                  );
                }
              } else {
                // If no days available, use weighted average of facility PPDs
                const weightedPPD = calculateWeightedAveragePPD(
                  facilities,
                  metricsAcrossFacilities
                );
                consolidatedMetric.PPD = weightedPPD;
              }
            }
            // For any other metrics, use weighted average
            else {
              const weightedPPD = calculateWeightedAveragePPD(
                facilities,
                metricsAcrossFacilities
              );
              consolidatedMetric.PPD = weightedPPD;
            }
          }

          // Add to consolidated data
          consolidatedFacility.periods[period].Categories[category].push(
            consolidatedMetric
          );
        });
      });
    });
  }

  // --- CONSOLIDATE HUD DEBT DATA ---

  // Process HUD Debt data
  const facilitiesWithHudDebt = facilities.filter(
    (facility) => facility.hudDebt && facility.hudDebt.Categories
  );

  if (facilitiesWithHudDebt.length > 0) {
    // 1. Process "HUD Debt" category if it exists
    const facilitiesWithHudDebtCategory = facilitiesWithHudDebt.filter(
      (facility) => facility.hudDebt?.Categories?.["HUD Debt"]?.length > 0
    );

    if (facilitiesWithHudDebtCategory.length > 0) {
      // Initialize HUD Debt category
      consolidatedFacility.hudDebt.Categories["HUD Debt"] = [];

      // Sum these fields
      ["Current Loan Balance", "Debt Service", "Original Loan"].forEach(
        (field) => {
          const sum = facilitiesWithHudDebtCategory.reduce(
            (total, facility) => {
              const item = facility.hudDebt.Categories["HUD Debt"]?.find(
                (i) => i.Type === field
              );
              return total + (parseFloat(item?.Amount) || 0);
            },
            0
          );

          consolidatedFacility.hudDebt.Categories["HUD Debt"].push({
            Type: field,
            Amount: sum,
          });
        }
      );

      // Average these fields
      ["Interest Rate"].forEach((field) => {
        const items = facilitiesWithHudDebtCategory
          .map((facility) =>
            facility.hudDebt.Categories["HUD Debt"]?.find(
              (i) => i.Type === field
            )
          )
          .filter(Boolean);

        if (items.length > 0) {
          const avg =
            items.reduce((sum, item) => sum + (parseFloat(item.Rate) || 0), 0) /
            items.length;

          consolidatedFacility.hudDebt.Categories["HUD Debt"].push({
            Type: field,
            Rate: avg,
          });
        }
      });

      // Average Term
      const termItems = facilitiesWithHudDebtCategory
        .map((facility) =>
          facility.hudDebt.Categories["HUD Debt"]?.find(
            (i) => i.Type === "Term"
          )
        )
        .filter(Boolean);

      if (termItems.length > 0) {
        const avgTerm =
          termItems.reduce(
            (sum, item) => sum + (parseFloat(item.Amount) || 0),
            0
          ) / termItems.length;

        consolidatedFacility.hudDebt.Categories["HUD Debt"].push({
          Type: "Term",
          Amount: Math.round(avgTerm * 100) / 100,
        });
      }

      // For Endorsement Date, use the middle date
      const dateItems = facilitiesWithHudDebtCategory
        .map((facility) =>
          facility.hudDebt.Categories["HUD Debt"]?.find(
            (i) => i.Type === "Endorsement Date"
          )
        )
        .filter(Boolean);

      if (dateItems.length > 0) {
        // Sort dates chronologically
        dateItems.sort((a, b) => {
          const dateA = new Date(a.Date);
          const dateB = new Date(b.Date);
          return dateA - dateB;
        });

        // Select the middle date
        const middleIndex = Math.floor(dateItems.length / 2);

        consolidatedFacility.hudDebt.Categories["HUD Debt"].push({
          Type: "Endorsement Date",
          Date: dateItems[middleIndex].Date,
        });
      }
    }

    // 2. Process Medicaid Rate categories
    const medCategories = new Set();

    // Find all Med* categories across facilities
    facilitiesWithHudDebt.forEach((facility) => {
      Object.keys(facility.hudDebt.Categories || {}).forEach((category) => {
        if (category.startsWith("Med")) {
          medCategories.add(category);
        }
      });
    });

    // Process each Med category
    [...medCategories].forEach((medCategory) => {
      // Initialize category if needed
      if (!consolidatedFacility.hudDebt.Categories[medCategory]) {
        consolidatedFacility.hudDebt.Categories[medCategory] = [];
      }

      // Get all metric types across facilities
      const metricTypes = new Set();
      facilitiesWithHudDebt.forEach((facility) => {
        const items = facility.hudDebt.Categories[medCategory] || [];
        items.forEach((item) => metricTypes.add(item.Type));
      });

      // Process each metric type with weighted average
      [...metricTypes].forEach((metricType) => {
        const itemsWithBeds = facilitiesWithHudDebt
          .map((facility) => {
            const item = facility.hudDebt.Categories[medCategory]?.find(
              (i) => i.Type === metricType
            );
            const beds = parseInt(facility.details?.["Total Beds"]) || 0;
            return item ? { item, beds } : null;
          })
          .filter(Boolean);

        if (itemsWithBeds.length > 0) {
          const totalBeds = itemsWithBeds.reduce(
            (sum, { beds }) => sum + beds,
            0
          );

          if (totalBeds > 0) {
            // Weighted average based on bed count
            const weightedAvg =
              itemsWithBeds.reduce((sum, { item, beds }) => {
                return sum + (parseFloat(item.Amount) || 0) * beds;
              }, 0) / totalBeds;

            consolidatedFacility.hudDebt.Categories[medCategory].push({
              Type: metricType,
              Amount: weightedAvg,
            });
          }
        }
      });
    });
  }

  return consolidatedFacility;
};

/**
 * Internal helper function for calculating PPD for metrics that require skilled patient days
 * Used by Therapy, Pharmacy expense, and Other Ancillaries calculations
 */
/**
 * Calculates PPD for metrics that require skilled patient days
 * @param {Object} consolidatedMetric - The consolidated metric object
 * @param {Object} consolidatedFacility - The consolidated facility object
 * @param {string} period - The period being processed
 * @returns {string|null} The calculated PPD value or null
 */
function _calculateSkilledMetricPPD(
  consolidatedMetric,
  consolidatedFacility,
  period
) {
  // Get the metrics for skilled days components
  const metricTypes = [
    "Medicare Days",
    "Medicare",
    "Medicare HMO Days",
    "Medicare HMO",
    "Commercial Insurance Days",
    "Commercial Insurance",
  ];

  // Find relevant metrics using our helper function
  const metrics = metricTypes
    .map((type) =>
      findMetric(
        consolidatedFacility.periods[period]?.Categories,
        "Occupancy",
        type
      )
    )
    .filter(Boolean);

  // Sum up skilled days
  const totalSkilledDays = metrics.reduce(
    (sum, metric) => sum + getMetricValue(metric, "Annual"),
    0
  );

  // If we have skilled days and an Annual value, calculate PPD
  if (totalSkilledDays > 0 && consolidatedMetric.Annual > 0) {
    const calculatedValue = parseFloat(
      (consolidatedMetric.Annual / totalSkilledDays).toFixed(2)
    );
    return `${calculatedValue} /Skilled day`;
  }

  // If we get here, we don't have enough data for a valid calculation
  return null;
}

/**
 * Calculates AL Nursing PPD using AL Total days as the denominator
 */
function calculateALNursingPPD(
  consolidatedMetric,
  consolidatedFacility,
  facilities,
  period
) {
  // First look for AL Total in consolidated data
  const alTotalMetric = consolidatedFacility.periods[
    period
  ]?.Categories?.Occupancy?.find((item) => item.Type === "AL Total");

  // Try to get AL Total days from consolidated metric
  let alTotalDays = alTotalMetric?.Annual || 0;

  // If we don't have AL Total days yet, gather from individual facilities
  if (alTotalDays === 0) {
    // Sum up AL Total days from facilities that have it
    alTotalDays = facilities.reduce((total, facility) => {
      const facilityAlTotal = facility.periods?.[
        period
      ]?.Categories?.Occupancy?.find((item) => item.Type === "AL Total");
      const facilityDays = facilityAlTotal?.Annual || 0;
      return total + parseFloat(facilityDays);
    }, 0);
  }

  if (alTotalDays > 0) {
    // Use AL Total days as denominator for AL Nursing metrics
    const calculatedPPD = parseFloat(
      (consolidatedMetric.Annual / alTotalDays).toFixed(2)
    );
    return calculatedPPD;
  }

  // If AL Total days not available, leave PPD as null (empty cell)
  return null;
}

/**
 * Calculates General Nursing PPD, excluding IL days from the total patient day count
 */
function calculateGeneralNursingPPD(
  consolidatedMetric,
  consolidatedFacility,
  facilities,
  period
) {
  // Get Total Annual from Occupancy
  const totalMetric = consolidatedFacility.periods[
    period
  ]?.Categories?.Occupancy?.find((item) => item.Type === "Total");

  // Get Independent Living Days from Occupancy
  const ilMetric = consolidatedFacility.periods[
    period
  ]?.Categories?.Occupancy?.find(
    (item) =>
      item.Type === "IL Total" ||
      item.Type === "Independent Living" ||
      item.Type.includes("IL ")
  );

  // Try to get Total days from consolidated metric
  let totalDays = parseFloat(totalMetric?.Annual) || 0;
  let ilDays = parseFloat(ilMetric?.Annual) || 0;

  // If consolidated Total days are missing, gather from individual facilities
  if (totalDays === 0) {
    totalDays = facilities.reduce((total, facility) => {
      const facilityTotalMetric = facility.periods?.[
        period
      ]?.Categories?.Occupancy?.find((item) => item.Type === "Total");
      const facilityDays = parseFloat(facilityTotalMetric?.Annual) || 0;
      return total + facilityDays;
    }, 0);
  }

  // If consolidated IL days are missing, gather from individual facilities
  if (ilDays === 0 && totalDays > 0) {
    ilDays = facilities.reduce((total, facility) => {
      const facilityILMetric = facility.periods?.[
        period
      ]?.Categories?.Occupancy?.find(
        (item) =>
          item.Type === "IL Total" ||
          item.Type === "Independent Living" ||
          item.Type.includes("IL ")
      );
      const facilityDays = parseFloat(facilityILMetric?.Annual) || 0;
      return total + facilityDays;
    }, 0);
  }

  // Calculate adjusted denominator (Total - IL days)
  const adjustedDays = totalDays - ilDays;

  if (adjustedDays > 0) {
    // Calculate PPD using adjusted days as denominator
    const calculatedPPD = parseFloat(
      (consolidatedMetric.Annual / adjustedDays).toFixed(2)
    );
    return calculatedPPD;
  }

  // If adjusted days not available or zero, leave PPD as null
  return null;
}

/**
 * Calculates standard PPD values for metrics that don't require special handling
 * Handles both numeric PPD values and string PPD values with text parts
 */
function calculateStandardPPD(
  consolidatedMetric,
  metricsAcrossFacilities,
  facilities,
  totalPatientDays,
  period
) {
  // Check if any of the metrics have a string PPD value that contains both number and text
  const hasStringPPD = metricsAcrossFacilities.some(
    (m) =>
      typeof m.PPD === "string" &&
      /\d+(\.\d+)?/.test(m.PPD) &&
      /[a-zA-Z]/.test(m.PPD)
  );

  if (hasStringPPD) {
    // If we have string PPDs with numbers and text, preserve the text part
    // and calculate the weighted average of the numeric parts
    const numericValues = metricsAcrossFacilities.map((m, index) => {
      if (typeof m.PPD === "string") {
        const match = m.PPD.match(/\d+(\.\d+)?/);
        return {
          value: match ? parseFloat(match[0]) : 0,
          patientDays:
            parseFloat(
              facilities[index]?.periods?.[period]?.Categories?.Occupancy?.find(
                (item) => item.Type === "Total"
              )?.Annual
            ) || 0,
        };
      }
      return {
        value: parseFloat(m.PPD) || 0,
        patientDays:
          parseFloat(
            facilities[index]?.periods?.[period]?.Categories?.Occupancy?.find(
              (item) => item.Type === "Total"
            )?.Annual
          ) || 0,
      };
    });

    // Calculate weighted average based on patient days
    const totalPatientDaysForWeighting = numericValues.reduce(
      (sum, { patientDays }) => sum + patientDays,
      0
    );

    let avgNumericValue;
    if (totalPatientDaysForWeighting > 0) {
      avgNumericValue =
        numericValues.reduce(
          (sum, { value, patientDays }) => sum + value * patientDays,
          0
        ) / totalPatientDaysForWeighting;
    }
    // Get the text part from the first metric that has it
    const textPart = metricsAcrossFacilities
      .find(
        (m) =>
          typeof m.PPD === "string" &&
          /\d+(\.\d+)?/.test(m.PPD) &&
          /[a-zA-Z]/.test(m.PPD)
      )
      ?.PPD?.replace(/\d+(\.\d+)?/, "")
      .trim();

    return `${parseFloat(avgNumericValue).toFixed(2)} ${textPart}`;
  }

  return parseFloat((consolidatedMetric.Annual / totalPatientDays).toFixed(2));
}

// Export the internal helper functions for use in other modules if needed
export {
  _calculateSkilledMetricPPD,
  calculateALNursingPPD,
  calculateGeneralNursingPPD,
  calculateStandardPPD,
};
